import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { updateDoc, Timestamp } from 'firebase/firestore';
import { TERMS_VERSION } from '../pages/TermsOfService';
import { PRIVACY_VERSION } from '../pages/PrivacyPolicy';
import { userProfileDocument } from '../lib/paths';

interface TermsAcceptanceModalProps {
  userId: string;
  onAccepted: () => void;
}

const TermsAcceptanceModal: React.FC<TermsAcceptanceModalProps> = ({
  userId,
  onAccepted,
}) => {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!accepted) return;
    setLoading(true);

    try {
      await updateDoc(userProfileDocument(userId), {
        termsAcceptedAt: Timestamp.now(),
        termsVersion: TERMS_VERSION,
        privacyAcceptedAt: Timestamp.now(),
        privacyVersion: PRIVACY_VERSION,
      });
      onAccepted();
    } catch {
      // If profile doesn't exist yet (old user), create it
      const { setDoc } = await import('firebase/firestore');
      const { auth } = await import('../firebase');
      await setDoc(userProfileDocument(userId), {
        email: auth.currentUser?.email || '',
        createdAt: Timestamp.now(),
        termsAcceptedAt: Timestamp.now(),
        termsVersion: TERMS_VERSION,
        privacyAcceptedAt: Timestamp.now(),
        privacyVersion: PRIVACY_VERSION,
      });
      onAccepted();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
              <svg
                className="w-4 h-4 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Aktualizacja regulaminu
            </h3>
          </div>

          <p className="text-gray-600 mb-4">
            Zaktualizowaliśmy regulamin i politykę prywatności. Przeczytaj i
            zaakceptuj, aby kontynuować korzystanie z aplikacji.
          </p>

          <div className="space-y-2 mb-6">
            <Link
              to="/terms"
              target="_blank"
              className="block text-orange-700 hover:text-orange-800 underline"
            >
              Regulamin
            </Link>
            <Link
              to="/privacy"
              target="_blank"
              className="block text-orange-700 hover:text-orange-800 underline"
            >
              Polityka Prywatności
            </Link>
          </div>

          <div className="flex items-start mb-6">
            <input
              id="accept-terms"
              type="checkbox"
              checked={accepted}
              onChange={e => setAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label
              htmlFor="accept-terms"
              className="ml-2 text-sm text-gray-600"
            >
              Akceptuję zaktualizowany regulamin i politykę prywatności
            </label>
          </div>

          <button
            onClick={handleAccept}
            disabled={!accepted || loading}
            className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Zapisywanie...' : 'Zaakceptuj'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptanceModal;
